import React, { useState, useRef, useEffect } from "react";
import { Globe, Check } from "lucide-react";
import { useLanguage, LANGUAGES } from "../lib/i18n";

/**
 * LanguageSwitcher — Globe icon + current lang label → opens a dropdown
 * panel with all supported languages. Closes on outside click / Escape.
 */
const LanguageSwitcher = ({ solid = true, compact = false }) => {
    const { lang, setLang, t } = useLanguage();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

    const triggerColor = solid
        ? "text-foreground hover:text-secondary"
        : "text-white hover:text-secondary";

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="lang-toggle"
                aria-label="Select language"
                aria-expanded={open}
                className={`flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase transition-colors ${triggerColor}`}
            >
                <Globe size={14} strokeWidth={1.5} />
                {compact ? current.label : (
                    <>
                        <span>{current.label}</span>
                    </>
                )}
            </button>

            {open && (
                <div
                    data-testid="lang-dropdown"
                    role="listbox"
                    className="absolute right-0 top-full mt-4 w-60 bg-background border border-border shadow-[0_16px_40px_rgba(0,0,0,0.12)] py-2 z-50"
                >
                    <div className="px-4 py-2 overline text-muted-foreground border-b border-border mb-1">
                        {t("label.language")}
                    </div>
                    {LANGUAGES.map((l) => {
                        const selected = l.code === lang;
                        return (
                            <button
                                key={l.code}
                                type="button"
                                role="option"
                                aria-selected={selected}
                                data-testid={`lang-option-${l.code}`}
                                onClick={() => {
                                    setLang(l.code);
                                    setOpen(false);
                                }}
                                className={`w-full flex items-center justify-between gap-4 px-4 py-2.5 text-left text-sm transition-colors ${
                                    selected
                                        ? "text-secondary font-medium"
                                        : "text-foreground hover:bg-muted"
                                }`}
                            >
                                <span className="flex items-center gap-3">
                                    <span className="inline-flex w-7 h-7 items-center justify-center text-[11px] tracking-wider bg-muted font-medium">
                                        {l.label}
                                    </span>
                                    <span className="font-body">{l.name}</span>
                                </span>
                                {selected && (
                                    <Check
                                        size={14}
                                        strokeWidth={2}
                                        className="text-secondary"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
